import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useSeoProperties from '@aurora/shared-client/components/seo/useSeoProperties';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import { stripHtmlFromString } from '@aurora/shared-utils/helpers/objects/StringHelper';
import type { QAPageJsonLdProps } from 'next-seo';
import { QAPageJsonLd } from 'next-seo';
import React, { useContext, useRef } from 'react';
import { UrlObject, useContextObjectRefFromUrl } from '../../context/useContextObjectFromUrl';
import qaPageSchemaDataQuery from './QAPageSchemaData.query.graphql';
import { queryVariables, transformSolution } from '../../../helpers/seo/QAPageSchemaHelper';
import type {
  QaPageSchemaDataQuery,
  QaPageSchemaDataQueryVariables
} from '../../../types/graphql-types';

/**
 * Renders QAPageSchema on Forum message Page which has replies and not on permalinked page.
 *
 * @author Jitiendran K S
 */
const QAPageSchema = () => {
  const { contextMessage } = useContext(AppContext);
  const { baseUrl } = useContext(TenantContext);
  const { router } = useEndUserRoutes();
  const { getCaseSensitivePath } = useSeoProperties();
  const { id: replyId } = useContextObjectRefFromUrl(UrlObject.REPLY);
  const isPermalinkPage = replyId !== null;
  const isMessageSolved = useRef(contextMessage.conversation.solved);
  const skipQaPageSchema =
    !contextMessage ||
    contextMessage?.depth !== 0 ||
    contextMessage?.repliesCount === 0 ||
    isPermalinkPage;

  const { data: acceptedSolution, loading: acceptedSolutionLoading } = useQueryWithTracing<
    QaPageSchemaDataQuery,
    QaPageSchemaDataQueryVariables
  >(module, qaPageSchemaDataQuery, {
    variables: queryVariables(contextMessage, true),
    skip: skipQaPageSchema || !isMessageSolved.current
  });

  const { data: suggestedSolution, loading: suggestedSolutionLoading } = useQueryWithTracing<
    QaPageSchemaDataQuery,
    QaPageSchemaDataQueryVariables
  >(module, qaPageSchemaDataQuery, {
    variables: queryVariables(contextMessage, false, 5),
    skip: skipQaPageSchema || isMessageSolved.current
  });

  if (acceptedSolutionLoading || suggestedSolutionLoading) {
    return null;
  }

  // Not a valid message for QA page schema
  if (skipQaPageSchema) {
    return null;
  }

  const qaPageJsonLd: QAPageJsonLdProps = {
    mainEntity: {
      name: stripHtmlFromString(contextMessage.subject),
      text: stripHtmlFromString(contextMessage.body),
      answerCount: contextMessage.repliesCount,
      upvoteCount: contextMessage.kudosSumWeight,
      dateCreated: contextMessage.postTime,
      author: { name: getCaseSensitivePath(contextMessage.author.login) }
    }
  };

  // If forum has replies and an accepted solution
  if (acceptedSolution) {
    const {
      messages: { edges: acceptedSolutionEdges }
    } = acceptedSolution;
    if (acceptedSolutionEdges.length > 0) {
      const [{ node: currentNode }] = acceptedSolutionEdges;
      qaPageJsonLd.mainEntity.acceptedAnswer = transformSolution(
        currentNode,
        router,
        baseUrl,
        getCaseSensitivePath
      );
    }
  }

  // If forum has replies
  if (suggestedSolution) {
    const {
      messages: { edges: suggestedSolutionEdges }
    } = suggestedSolution;
    if (suggestedSolutionEdges.length > 0) {
      qaPageJsonLd.mainEntity.suggestedAnswer = suggestedSolutionEdges.map(suggestedNode =>
        transformSolution(suggestedNode.node, router, baseUrl, getCaseSensitivePath)
      );
    }
  }

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <QAPageJsonLd {...qaPageJsonLd} />
  );
};

export default QAPageSchema;
